<template>
  <p
    class="package-builder-countdown"
    :class="{ 'package-builder-countdown--complete': isComplete }"
    aria-live="polite"
  >
    <template v-if="isComplete">{{ completeMessage }}</template>
    <template v-else>
      <span v-if="underBefore">{{ underBefore }}</span>
      <span class="package-builder-countdown__count">{{ remainingCount }}</span>
      <span v-if="underAfter">{{ underAfter }}</span>
    </template>
  </p>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import { useAppContentStore } from '@/stores/appContent'
import { usePackageBuilderStore } from '@/stores/packageBuilder'
import { mapState, mapStores } from 'pinia'

import {
  type BuilderContent,
  resolveBuilderContent
} from '@/components/Senior/PackageBuilder/content'

// The under-target template carries a single `##PackageRemainingCount##`
// placeholder that we render bold. Splitting around the marker lets us keep
// the template string intact in `content.ts` (and Contentful) while applying
// the inline styling here in the component.
const REMAINING_PLACEHOLDER = '##PackageRemainingCount##'

export default defineComponent({
  name: 'PackageBuilderCountdown',
  props: {
    // The cart bundle whose sheet-selection progress this countdown reflects.
    cartBundleId: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapStores(useAppContentStore),
    ...mapState(usePackageBuilderStore, ['remainingForCartBundle', 'isValidForCartBundle']),
    content(): BuilderContent {
      return resolveBuilderContent(this.appContentStore.seniorPackageBuilderContent)
    },
    remainingCount(): number {
      return this.remainingForCartBundle(this.cartBundleId)
    },
    isComplete(): boolean {
      return this.isValidForCartBundle(this.cartBundleId)
    },
    // Trimmed because the count is now a flex item (the magenta pill) rather
    // than inline text — the row's `gap` supplies the spacing the template's
    // literal spaces used to, and keeping both would double it.
    underBefore(): string {
      return this.content.countdownTemplateUnder.split(REMAINING_PLACEHOLDER)[0].trim()
    },
    underAfter(): string {
      return (this.content.countdownTemplateUnder.split(REMAINING_PLACEHOLDER)[1] ?? '').trim()
    },
    completeMessage(): string {
      return this.content.countdownTemplateComplete
    }
  }
})
</script>

<style scoped lang="scss">
// Figma "counter" banner (node 10566:31685) — a bordered grey strip inside the
// selection card, not the free-floating pill this used to be.
// Figma "counter" banner (node 11279:35276) — a bordered grey strip inside the
// selection card, with the remaining count called out in a magenta circle.
.package-builder-countdown {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0;
  padding: 0.5rem 1.125rem;
  background: #eee;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1.125rem;
  line-height: 1.75rem;
  color: #000;

  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    // Figma draws this at 24px, which crops a two-digit remainder at our text
    // size — 28px is the smallest that keeps "15" clear of the edge. `min-width`
    // + a pill radius rather than a hard `width`, so a three-digit remainder
    // widens into a lozenge instead of overflowing. The padding only engages at
    // three digits; one and two digits both sit inside `min-width`, so the shape
    // stays a true circle and the digits stay centered.
    min-width: 1.75rem;
    height: 1.75rem;
    padding: 0 0.1875rem;
    border-radius: 999px;
    background: #bb4a92;
    color: #fff;
    // Overrides the 1.75rem inherited from the banner, which would otherwise
    // push the digits out of a 1.5rem-tall circle.
    line-height: 1;
    // No negative letter-spacing here: it is applied after the final glyph too,
    // so the flex centering counts that trailing sliver as content and shifts
    // the digits visibly left of center. Tabular figures keep "11" balanced the
    // same way "48" is.
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    text-align: center;
  }

  &--complete {
    color: #bb4a92;
    font-weight: 600;
  }
}
</style>
